import React, { useState, useEffect, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Pagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  EventRepeat as RecurringIcon,
  LocalShipping as PickupIcon,
  Cancel as CancelIcon,
  PlayArrow as StartIcon,
  CheckCircleOutline as ConfirmIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ISchedule, ScheduleStatus } from "../types";
import { getSchedules, updateScheduleStatus } from "../services";

const ServicosEmAndamento: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("TODOS");
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;
  const [anchorEl, setAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [updatingStatus, setUpdatingStatus] = useState<{
    [key: string]: boolean;
  }>({});

  // Função para carregar serviços
  const loadServicos = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // Buscar agendamentos com o filtro de status selecionado
      const response = await getSchedules({
        status: statusFilter === "TODOS" ? undefined : statusFilter,
      });

      // Ordenar por data mais recente primeiro
      const sortedSchedules = response.sort((a: ISchedule, b: ISchedule) => {
        const dateA = new Date(a.scheduledAt).getTime();
        const dateB = new Date(b.scheduledAt).getTime();
        return dateB - dateA; // Mais recente primeiro
      });

      setSchedules(sortedSchedules);
    } catch (err) {
      setError("Erro ao carregar serviços.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Carregar dados ao montar o componente
  useEffect(() => {
    loadServicos();
  }, [loadServicos]);

  // Auto-refresh a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      //
      loadServicos();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [loadServicos]);

  // Handlers do menu
  const handleMenuOpen = (
    scheduleId: string,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl({ ...anchorEl, [scheduleId]: event.currentTarget });
  };

  const handleMenuClose = (scheduleId: string) => {
    setAnchorEl({ ...anchorEl, [scheduleId]: null });
  };

  // Handler genérico para mudar status
  const handleChangeStatus = async (
    scheduleId: string,
    newStatus: ScheduleStatus
  ) => {
    handleMenuClose(scheduleId);
    setUpdatingStatus((prev) => ({ ...prev, [scheduleId]: true }));

    try {
      await updateScheduleStatus(scheduleId, newStatus);

      // Recarregar a lista para refletir mudanças
      await loadServicos();

      // Resetar o estado de loading após sucesso
      setUpdatingStatus((prev) => ({ ...prev, [scheduleId]: false }));
    } catch (err) {
      setError("Erro ao atualizar status. Tente novamente.");
      setUpdatingStatus((prev) => ({ ...prev, [scheduleId]: false }));
    }
  };

  // Handler para concluir serviço (mantido por compatibilidade)
  const handleConcluir = async (scheduleId: string) => {
    await handleChangeStatus(scheduleId, ScheduleStatus.CONCLUIDO);
  };

  // Handler para filtro de status
  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(1); // Reset para primeira página ao mudar filtro
  };

  // Calcular agendamentos paginados
  const paginatedSchedules = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return schedules.slice(startIndex, endIndex);
  }, [schedules, page, itemsPerPage]);

  // Calcular total de páginas
  const totalPages = useMemo(() => {
    return Math.ceil(schedules.length / itemsPerPage);
  }, [schedules.length, itemsPerPage]);

  // Handler para mudança de página
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    // Scroll para o topo ao mudar de página
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Função para obter cor do status
  const getStatusColor = (
    status: ScheduleStatus
  ): "default" | "primary" | "secondary" | "error" | "success" | "warning" => {
    switch (status) {
      case ScheduleStatus.PENDENTE:
        return "default";
      case ScheduleStatus.CONFIRMADO:
        return "primary";
      case ScheduleStatus.EM_ANDAMENTO:
        return "warning";
      case ScheduleStatus.CONCLUIDO:
        return "success";
      case ScheduleStatus.CANCELADO:
        return "error";
      default:
        return "default";
    }
  };

  // Função para obter label do status
  const getStatusLabel = (status: ScheduleStatus): string => {
    switch (status) {
      case ScheduleStatus.PENDENTE:
        return "Pendente";
      case ScheduleStatus.CONFIRMADO:
        return "Confirmado";
      case ScheduleStatus.EM_ANDAMENTO:
        return "Em Andamento";
      case ScheduleStatus.CONCLUIDO:
        return "Concluído";
      case ScheduleStatus.CANCELADO:
        return "Cancelado";
      default:
        return status;
    }
  };

  // Estado de carregamento
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        pt: 0,
        mt: { xs: -2, sm: -2, md: -2.5 },
        width: "100%",
        maxWidth: "1920px",
        minWidth: 0,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header com Título, Filtro e Contador */}
      <Box
        sx={{
          mb: { xs: 2, sm: 2, md: 2.5 },
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: { xs: 2, sm: 2, md: 3 },
          alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
          justifyContent: "space-between",
        }}
      >
        {/* Título e Descrição */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? "h5" : "h4"} color="text.primary">
            Gerenciar Serviços
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            color="text.secondary"
          >
            Visualize e gerencie agendamentos por status
          </Typography>
        </Box>

        {/* Filtro e Contador */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 2 },
            alignItems: { xs: "stretch", sm: "center" },
            width: { xs: "100%", sm: "auto", md: "auto" },
          }}
        >
          <FormControl
            sx={{ minWidth: { xs: "100%", sm: 200, md: 250 } }}
            size={isMobile ? "small" : "medium"}
          >
            <InputLabel>Filtrar por Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleFilterChange}
              label="Filtrar por Status"
            >
              <MenuItem value="TODOS">
                {isMobile ? "Todos" : "Todos os Status"}
              </MenuItem>
              <MenuItem value={ScheduleStatus.PENDENTE}>Pendente</MenuItem>
              <MenuItem value={ScheduleStatus.CONFIRMADO}>Confirmado</MenuItem>
              <MenuItem value={ScheduleStatus.EM_ANDAMENTO}>
                Em Andamento
              </MenuItem>
              <MenuItem value={ScheduleStatus.CONCLUIDO}>Concluído</MenuItem>
              <MenuItem value={ScheduleStatus.CANCELADO}>Cancelado</MenuItem>
            </Select>
          </FormControl>

          <Chip
            label={`${schedules.length} ${
              schedules.length === 1 ? "agendamento" : "agendamentos"
            }`}
            color="primary"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
          />
        </Box>
      </Box>

      {/* Mensagem de erro */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Estado vazio */}
      {schedules.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            textAlign: "center",
            bgcolor: "grey.50",
            borderRadius: 2,
          }}
        >
          <PetsIcon
            sx={{
              fontSize: { xs: 48, sm: 56, md: 64 },
              color: "grey.400",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            color="text.secondary"
            gutterBottom
          >
            Nenhum agendamento encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Não há agendamentos com o status selecionado.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Container com scroll para o grid de cards */}
          <Box
            sx={{
              height: {
                xs: "calc(100vh - 340px)",
                sm: "calc(100vh - 350px)",
                md: "calc(100vh - 320px)",
                lg: "calc(100vh - 320px)",
                xl: "calc(100vh - 300px)",
              },
              overflowY: {
                xs: "auto",
                sm: "auto",
                md: "auto",
                lg: "auto",
                xl: "auto",
              },
              mb: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            {/* Grid de cards */}
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {paginatedSchedules.map((schedule) => (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    lg: 4,
                    xl: 4,
                  }}
                  key={schedule.id}
                >
                  <Card
                    elevation={3}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    {/* Badge de Status no canto superior esquerdo */}
                    <Chip
                      label={getStatusLabel(schedule.status)}
                      color={getStatusColor(schedule.status)}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        fontWeight: 600,
                        zIndex: 1,
                      }}
                    />

                    {/* Menu de opções */}
                    <IconButton
                      sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                      onClick={(e) => handleMenuOpen(schedule.id, e)}
                    >
                      <MoreIcon />
                    </IconButton>

                    <Menu
                      anchorEl={anchorEl[schedule.id]}
                      open={Boolean(anchorEl[schedule.id])}
                      onClose={() => handleMenuClose(schedule.id)}
                    >
                      <MenuItem
                        onClick={() =>
                          handleChangeStatus(
                            schedule.id,
                            ScheduleStatus.CONFIRMADO
                          )
                        }
                        disabled={schedule.status === ScheduleStatus.CONFIRMADO}
                      >
                        <ConfirmIcon sx={{ mr: 1, fontSize: 20 }} />
                        Confirmar
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleChangeStatus(
                            schedule.id,
                            ScheduleStatus.EM_ANDAMENTO
                          )
                        }
                        disabled={
                          schedule.status === ScheduleStatus.EM_ANDAMENTO
                        }
                      >
                        <StartIcon sx={{ mr: 1, fontSize: 20 }} />
                        Iniciar Serviço
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          handleChangeStatus(
                            schedule.id,
                            ScheduleStatus.CONCLUIDO
                          )
                        }
                        disabled={schedule.status === ScheduleStatus.CONCLUIDO}
                      >
                        <CheckIcon sx={{ mr: 1, fontSize: 20 }} />
                        Concluir
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={() =>
                          handleChangeStatus(
                            schedule.id,
                            ScheduleStatus.CANCELADO
                          )
                        }
                        disabled={schedule.status === ScheduleStatus.CANCELADO}
                        sx={{ color: "error.main" }}
                      >
                        <CancelIcon sx={{ mr: 1, fontSize: 20 }} />
                        Cancelar
                      </MenuItem>
                    </Menu>

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        pb: 1,
                        px: { xs: 2, sm: 2, md: 2 },
                        pt: { xs: 2, sm: 2, md: 2 },
                      }}
                    >
                      {/* Foto e Nome do Pet */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: { xs: 1.5, sm: 2 },
                        }}
                      >
                        <Avatar
                          src={schedule.pet?.photo || schedule.pet?.foto}
                          sx={{
                            width: { xs: 56, sm: 60, md: 64 },
                            height: { xs: 56, sm: 60, md: 64 },
                            mr: { xs: 1.5, sm: 2 },
                            border: "3px solid",
                            borderColor: "primary.main",
                          }}
                        >
                          <PetsIcon
                            sx={{ fontSize: { xs: 28, sm: 30, md: 32 } }}
                          />
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {schedule.pet?.name}
                          </Typography>
                          <Typography
                            variant={isMobile ? "caption" : "body2"}
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {schedule.pet?.breed || "Raça não informada"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              textTransform: "capitalize",
                              display: { xs: "none", sm: "block" },
                            }}
                          >
                            {schedule.pet?.species || "Espécie não informada"}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

                      {/* Informações do serviço */}
                      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ fontSize: { xs: 9, sm: 10 } }}
                        >
                          Serviço
                        </Typography>
                        <Typography
                          variant={isMobile ? "body2" : "body1"}
                          sx={{ fontWeight: 500 }}
                        >
                          {schedule.serviceName}
                        </Typography>
                      </Box>

                      {/* Observações - Campo CRUCIAL */}
                      {schedule.notes && (
                        <Box
                          sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: "white",
                            borderRadius: 1,
                            borderLeft: 4,
                            borderColor: "warning.main",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: "warning.dark",
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            OBSERVAÇÕES IMPORTANTES
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "warning.dark" }}
                          >
                            {schedule.notes}
                          </Typography>
                        </Box>
                      )}

                      {/* Tutor */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <PersonIcon
                          sx={{ fontSize: 18, mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {schedule.pet?.owner?.name ||
                            schedule.pet?.tutor?.name ||
                            "Tutor não informado"}
                        </Typography>
                      </Box>

                      {/* Badges */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mt: 2,
                        }}
                      >
                        {schedule.requiresPickup && (
                          <Chip
                            size="small"
                            icon={<PickupIcon />}
                            label="Requer Busca"
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {schedule.isRecurring && (
                          <Chip
                            size="small"
                            icon={<RecurringIcon />}
                            label="Recorrente"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Data/Hora */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 2 }}
                      >
                        Entrada:{" "}
                        {format(
                          new Date(schedule.scheduledAt),
                          "dd/MM/yyyy 'às' HH:mm",
                          { locale: ptBR }
                        )}
                      </Typography>
                    </CardContent>

                    <Divider />

                    {/* Ações Condicionais */}
                    <CardActions
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        pt: { xs: 1, sm: 1.5 },
                      }}
                    >
                      {schedule.status === ScheduleStatus.PENDENTE && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          size={isMobile ? "small" : "medium"}
                          startIcon={
                            updatingStatus[schedule.id] ? (
                              <CircularProgress
                                size={isMobile ? 16 : 18}
                                color="inherit"
                              />
                            ) : (
                              <ConfirmIcon />
                            )
                          }
                          onClick={() =>
                            handleChangeStatus(
                              schedule.id,
                              ScheduleStatus.CONFIRMADO
                            )
                          }
                          disabled={updatingStatus[schedule.id]}
                          sx={{
                            fontWeight: 600,
                            textTransform: "none",
                            py: { xs: 0.75, sm: 1 },
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          {updatingStatus[schedule.id]
                            ? "Confirmando..."
                            : isMobile
                              ? "Confirmar"
                              : "Confirmar Agendamento"}
                        </Button>
                      )}

                      {schedule.status === ScheduleStatus.CONFIRMADO && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="warning"
                          size={isMobile ? "small" : "medium"}
                          startIcon={
                            updatingStatus[schedule.id] ? (
                              <CircularProgress
                                size={isMobile ? 16 : 18}
                                color="inherit"
                              />
                            ) : (
                              <StartIcon />
                            )
                          }
                          onClick={() =>
                            handleChangeStatus(
                              schedule.id,
                              ScheduleStatus.EM_ANDAMENTO
                            )
                          }
                          disabled={updatingStatus[schedule.id]}
                          sx={{
                            fontWeight: 600,
                            textTransform: "none",
                            py: { xs: 0.75, sm: 1 },
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          {updatingStatus[schedule.id]
                            ? "Iniciando..."
                            : isMobile
                              ? "Iniciar"
                              : "Iniciar Serviço"}
                        </Button>
                      )}

                      {schedule.status === ScheduleStatus.EM_ANDAMENTO && (
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          size={isMobile ? "small" : "medium"}
                          startIcon={
                            updatingStatus[schedule.id] ? (
                              <CircularProgress
                                size={isMobile ? 16 : 18}
                                color="inherit"
                              />
                            ) : (
                              <CheckIcon />
                            )
                          }
                          onClick={() => handleConcluir(schedule.id)}
                          disabled={updatingStatus[schedule.id]}
                          sx={{
                            fontWeight: 600,
                            textTransform: "none",
                            py: { xs: 0.75, sm: 1 },
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          {updatingStatus[schedule.id]
                            ? "Concluindo..."
                            : isMobile
                              ? "Concluir"
                              : "Concluir Serviço"}
                        </Button>
                      )}

                      {(schedule.status === ScheduleStatus.CONCLUIDO ||
                        schedule.status === ScheduleStatus.CANCELADO) && (
                        <Box
                          sx={{
                            width: "100%",
                            textAlign: "center",
                            py: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {schedule.status === ScheduleStatus.CONCLUIDO
                              ? "Serviço finalizado"
                              : "Agendamento cancelado"}
                          </Typography>
                        </Box>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Paginação - Fora da área de scroll */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : isTablet ? "medium" : "large"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={isMobile ? 1 : 1}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ServicosEmAndamento;
